import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import MarkInactive from "./MarkInactive";
import ReactivateStudent from "./ReactivateStudent";
import "./ViewStudents.css";
import OverrideFees from "./OverrideFees";
import FeeBreakdown from "./FeeBreakdown";
import EditStudent from "./EditStudent"; // â† ADDED THIS

function ViewStudents({
  onEditStudent,
  onRecordPayment,
  onClose,
  initialFilters = null,
}) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [markingInactive, setMarkingInactive] = useState(null);
  const [reactivatingStudent, setReactivatingStudent] = useState(null);

  const [filterSchool, setFilterSchool] = useState(
    initialFilters?.school || "all",
  );
  const [filterStatus, setFilterStatus] = useState(
    initialFilters?.paymentStatus || "all",
  );
  const [filterActive, setFilterActive] = useState("active");
  const [searchTerm, setSearchTerm] = useState(initialFilters?.search || "");
  const [overridingStudent, setOverridingStudent] = useState(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null); // â† ADDED THIS

  useEffect(() => {
    loadData();
  }, [filterActive]);

  useEffect(() => {
    applyFilters();
  }, [students, filterSchool, filterStatus, searchTerm, filterActive]);

  // Calculate TERM FEES owing (excluding admin) - WITH OVERRIDE SUPPORT
  const calculateTermFeesOwing = (student, termStructure) => {
    if (!student || !termStructure || !termStructure.termDates) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const studentStartDate = student.startDate?.toDate
      ? student.startDate.toDate()
      : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);

    const currentYear = today.getFullYear();
    let totalTermFees = 0;

    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];

    // Check each term - ONLY term fees
    for (let termNum = 1; termNum <= 4; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split("-").map(Number);
      const [endMonth, endDay] = termData.end.split("-").map(Number);

      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);

      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      const shouldCharge =
        studentStartDate <= termEnd &&
        today >= termStart &&
        (student.isActive ||
          (student.deactivationInfo?.deactivatedDate &&
            new Date(student.deactivationInfo.deactivatedDate) > termStart));

      if (shouldCharge) {
        // Check for override first
        const override = feeOverrides.find(
          (o) =>
            o.termNumber === termNum &&
            (o.overrideType === "term" || o.overrideType === "both"),
        );

        if (override && override.termFee !== undefined) {
          // Use override amount
          totalTermFees += override.termFee;
        } else {
          // Use standard term fee
          totalTermFees += termFee;
        }
      }
    }

    // Calculate total term fees paid from new termPayments structure
    const termPayments = student.termPayments || {};
    const termFeesPaid =
      (termPayments.term1 || 0) +
      (termPayments.term2 || 0) +
      (termPayments.term3 || 0) +
      (termPayments.term4 || 0);

    totalTermFees -= termFeesPaid;

    return Math.max(0, totalTermFees);
  };

  // Calculate ADMIN FEE owing separately - WITH OVERRIDE SUPPORT
  const calculateAdminFeeOwing = (student) => {
    const feeOverrides = student.feeOverrides || [];

    // Check if there's an admin fee override
    const adminOverride = feeOverrides.find(
      (o) => o.overrideType === "admin" || o.overrideType === "both",
    );

    let adminFee;
    if (adminOverride && adminOverride.adminFee !== undefined) {
      // Use override amount
      adminFee = adminOverride.adminFee;
    } else {
      // Use standard admin fee
      adminFee = student.adminFee || 0;
    }

    const adminFeePaid = student.termPayments?.admin || 0;
    return Math.max(0, adminFee - adminFeePaid);
  };

  // Calculate per-term breakdown for detailed display
  const calculateTermBreakdown = (student, termStructure) => {
    // Get fee overrides if any
    const feeOverrides = student.feeOverrides || [];

    // Check for admin fee override
    const adminOverride = feeOverrides.find(
      (o) => o.overrideType === "admin" || o.overrideType === "both",
    );

    // Use override admin fee if it exists, otherwise use standard
    const adminFeeDue = adminOverride?.adminFee ?? (student.adminFee || 0);

    const breakdown = {
      admin: {
        due: adminFeeDue,
        paid: student.termPayments?.admin || 0,
        balance: 0,
      },
      terms: [],
    };

    // Calculate admin balance
    breakdown.admin.balance = breakdown.admin.due - breakdown.admin.paid;

    const standardTermFee = student.feeStructure?.standardFee || 0;

    // Calculate for each term
    if (termStructure && termStructure.termDates) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const studentStartDate = student.startDate?.toDate
        ? student.startDate.toDate()
        : new Date(student.startDate);
      studentStartDate.setHours(0, 0, 0, 0);

      const currentYear = today.getFullYear();

      for (let termNum = 1; termNum <= 4; termNum++) {
        const termData = termStructure.termDates[`term${termNum}`];

        if (!termData || !termData.start || !termData.end) {
          // Term doesn't exist in structure
          breakdown.terms.push({
            termNum,
            due: 0,
            paid: student.termPayments?.[`term${termNum}`] || 0,
            balance: 0,
            shouldCharge: false,
          });
          continue;
        }

        const [startMonth, startDay] = termData.start.split("-").map(Number);
        const [endMonth, endDay] = termData.end.split("-").map(Number);

        const termStart = new Date(currentYear, startMonth - 1, startDay);
        termStart.setHours(0, 0, 0, 0);

        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        termEnd.setHours(23, 59, 59, 999);

        const shouldCharge =
          studentStartDate <= termEnd &&
          today >= termStart &&
          (student.isActive ||
            (student.deactivationInfo?.deactivatedDate &&
              new Date(student.deactivationInfo.deactivatedDate) > termStart));

        // Check for fee override
        const override = feeOverrides.find(
          (o) =>
            o.termNumber === termNum &&
            (o.overrideType === "term" || o.overrideType === "both"),
        );

        let termDue = 0;
        if (shouldCharge) {
          termDue =
            override && override.termFee !== undefined
              ? override.termFee
              : standardTermFee;
        }

        const termPaid = student.termPayments?.[`term${termNum}`] || 0;

        breakdown.terms.push({
          termNum,
          due: termDue,
          paid: termPaid,
          balance: termDue - termPaid,
          shouldCharge,
        });
      }
    }

    return breakdown;
  };

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, "Franchisees", user.uid);

      // Load schools
      const schoolsQuery = query(
        collection(db, "schools"),
        where("franchiseeRef", "==", franchiseeRef),
        where("isActive", "==", true),
      );
      const schoolsSnapshot = await getDocs(schoolsQuery);
      const schoolsList = schoolsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSchools(schoolsList);

      // Load students based on filter
      let studentsQuery;
      if (filterActive === "active") {
        studentsQuery = query(
          collection(db, "students"),
          where("franchiseeRef", "==", franchiseeRef),
          where("isActive", "==", true),
        );
      } else if (filterActive === "inactive") {
        studentsQuery = query(
          collection(db, "students"),
          where("franchiseeRef", "==", franchiseeRef),
          where("isActive", "==", false),
        );
      } else {
        studentsQuery = query(
          collection(db, "students"),
          where("franchiseeRef", "==", franchiseeRef),
        );
      }

      const studentsSnapshot = await getDocs(studentsQuery);

      // Calculate real-time owing for each student
      const studentsWithCalculatedOwing = await Promise.all(
        studentsSnapshot.docs.map(async (docSnap) => {
          const studentData = { id: docSnap.id, ...docSnap.data() };

          // Get school and term structure
          if (studentData.schoolRef) {
            try {
              const schoolDoc = await getDoc(studentData.schoolRef);
              if (schoolDoc.exists()) {
                const schoolData = schoolDoc.data();

                if (schoolData.termStructureRef) {
                  const termStructureDoc = await getDoc(
                    schoolData.termStructureRef,
                  );
                  if (termStructureDoc.exists()) {
                    const termStructure = termStructureDoc.data();

                    // Calculate separate owing amounts
                    const termFeesOwing = calculateTermFeesOwing(
                      studentData,
                      termStructure,
                    );
                    const adminFeeOwing = calculateAdminFeeOwing(studentData);

                    return {
                      ...studentData,
                      termFeesOwing: termFeesOwing,
                      adminFeeOwing: adminFeeOwing,
                      calculatedOwing: termFeesOwing + adminFeeOwing,
                      _hasCalculation: true,
                      _termStructure: termStructure, // Store for detailed breakdown
                    };
                  }
                }
              }
            } catch (error) {
              console.error(
                "Error calculating owing for student:",
                studentData.id,
                error,
              );
            }
          }

          return {
            ...studentData,
            termFeesOwing: studentData.termFeesOwing || 0,
            adminFeeOwing: calculateAdminFeeOwing(studentData),
            calculatedOwing:
              (studentData.termFeesOwing || 0) +
              calculateAdminFeeOwing(studentData),
            _hasCalculation: false,
          };
        }),
      );

      // Sort by name
      studentsWithCalculatedOwing.sort((a, b) => {
        const nameA = a.studentName?.toLowerCase() || "";
        const nameB = b.studentName?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

      setStudents(studentsWithCalculatedOwing);
      setLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Error loading students");
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];

    if (filterSchool !== "all") {
      filtered = filtered.filter((s) => s.schoolRef?.id === filterSchool);
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((s) => s.paymentStatus === filterStatus);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((s) => {
        const name = s.studentName?.toLowerCase() || "";
        const account = s.accountNumber?.toLowerCase() || "";
        const contact = s.primaryContact?.name?.toLowerCase() || "";
        return (
          name.includes(search) ||
          account.includes(search) ||
          contact.includes(search)
        );
      });
    }

    setFilteredStudents(filtered);
  };

  const handleMarkInactive = (student) => {
    setMarkingInactive(student);
  };

  const handleReactivate = (student) => {
    setReactivatingStudent(student);
  };

  const handleOverride = (student) => {
    setOverridingStudent(student);
  };

  const handleRecordPayment = (studentId) => {
    if (onRecordPayment) {
      onRecordPayment(studentId);
    }
  };

  // â† UPDATED THIS FUNCTION
  const handleEditStudent = (studentId) => {
    // Find the full student object
    const student = students.find((s) => s.id === studentId);
    if (student) {
      setEditingStudent(student);
    }
  };

  const toggleRow = (studentId) => {
    setExpandedRow(expandedRow === studentId ? null : studentId);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return <div className="loading">Loading students...</div>;
  }

  return (
    <div className="view-students-container">
      <div className="view-students-header">
        <div
          className="header-top"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1>{"\u{1F4CB}"} View Students</h1>
          <button
            onClick={() => setFiltersCollapsed(!filtersCollapsed)}
            style={{
              background: "#4169E1",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            {filtersCollapsed ? "\u25BC Show Filters" : "\u25B2 Hide Filters"}
          </button>
        </div>

        {!filtersCollapsed && (
          <>
            <div className="filters-container">
              <div className="filter-group">
                <label>Student Status:</label>
                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  className="filter-select"
                >
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                  <option value="all">All Students</option>
                </select>
              </div>

              <div className="filter-group">
                <label>School:</label>
                <select
                  value={filterSchool}
                  onChange={(e) => setFilterSchool(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Schools</option>
                  {schools.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.schoolName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Payment Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="outstanding">Outstanding</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div className="filter-group search-group">
                <label>Search:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, Account #, Contact..."
                  className="search-input"
                />
              </div>
            </div>

            <div className="stats-bar">
              <div className="stat-item">
                <span className="stat-label">Total Students:</span>
                <span className="stat-value">{filteredStudents.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Owing:</span>
                <span className="stat-value">
                  {formatCurrency(
                    filteredStudents.reduce(
                      (sum, s) => sum + (s.calculatedOwing || 0),
                      0,
                    ),
                  )}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {filteredStudents.length === 0 ? (
        <div className="no-results">
          <p>No students found matching your filters.</p>
        </div>
      ) : (
        <div className="students-table-container">
          <table className="students-table">
            <thead>
              <tr>
                <th>Account #</th>
                <th>Student Name</th>
                <th>School</th>
                <th>Total Owing</th>
                <th>Payment Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <React.Fragment key={student.id}>
                  <tr className={!student.isActive ? "inactive-row" : ""}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {student.accountNumber || "N/A"}
                    </td>

                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <span style={{ fontWeight: "500" }}>
                          {student.studentName}
                        </span>
                        {!student.isActive && (
                          <span
                            style={{
                              display: "inline-block",
                              background: "#fee",
                              color: "#c00",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              width: "fit-content",
                            }}
                          >
                            {"\u{1F4A4}"} INACTIVE
                          </span>
                        )}
                      </div>
                    </td>

                    <td>{student.schoolName || "N/A"}</td>

                    <td
                      style={{
                        fontWeight: "700",
                        color:
                          (student.calculatedOwing || 0) > 0
                            ? "#e74c3c"
                            : "#27ae60",
                        textAlign: "right",
                        fontSize: "15px",
                      }}
                    >
                      {formatCurrency(student.calculatedOwing || 0)}
                      {student._hasCalculation && (
                        <span
                          style={{
                            display: "block",
                            fontSize: "10px",
                            color: "#27ae60",
                            fontWeight: "400",
                          }}
                        ></span>
                      )}
                    </td>

                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          ...(student.paymentStatus === "paid"
                            ? {
                                background: "#d4edda",
                                color: "#155724",
                              }
                            : student.paymentStatus === "partial"
                              ? {
                                  background: "#fff3cd",
                                  color: "#856404",
                                }
                              : {
                                  background: "#f8d7da",
                                  color: "#721c24",
                                }),
                        }}
                      >
                        {student.paymentStatus || "outstanding"}
                      </span>
                    </td>

                    <td style={{ padding: "4px" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <button
                          onClick={() => toggleRow(student.id)}
                          style={{
                            background: "#95a5a6",
                            color: "white",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                            minWidth: "35px",
                          }}
                          title="View Details"
                        >
                          {expandedRow === student.id ? "\u25B2" : "\u25BC"}
                        </button>

                        <button
                          onClick={() => handleOverride(student)}
                          disabled={!student.isActive}
                          style={{
                            background: student.isActive ? "#9b59b6" : "#ccc",
                            color: "white",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            cursor: student.isActive
                              ? "pointer"
                              : "not-allowed",
                            fontSize: "14px",
                            minWidth: "35px",
                          }}
                          title="Override Fees (Pro-Rata)"
                        >
                          {"\u2699\uFE0F"}
                        </button>

                        <button
                          onClick={() => handleRecordPayment(student.id)}
                          disabled={!student.isActive}
                          style={{
                            background: student.isActive ? "#2ecc71" : "#ccc",
                            color: "white",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            cursor: student.isActive
                              ? "pointer"
                              : "not-allowed",
                            fontSize: "14px",
                            minWidth: "35px",
                          }}
                          title="Record Payment"
                        >
                          {"\u{1F4B5}"}
                        </button>

                        <button
                          onClick={() => handleEditStudent(student.id)}
                          style={{
                            background: "#f39c12",
                            color: "white",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                            minWidth: "35px",
                          }}
                          title="Edit Student"
                        >
                          {"\u270F\uFE0F"}
                        </button>

                        {student.isActive ? (
                          <button
                            onClick={() => handleMarkInactive(student)}
                            style={{
                              background: "#e74c3c",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "14px",
                              minWidth: "35px",
                            }}
                            title="Mark Inactive"
                          >
                            {"\u{1F6AB}"}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(student)}
                            style={{
                              background: "#27ae60",
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "14px",
                              minWidth: "35px",
                            }}
                            title="Reactivate Student"
                          >
                            {"\u2705"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expandedRow === student.id && (
                    <tr className="expanded-row">
                      <td colSpan="6">
                        <div className="expanded-content">
                          <div className="details-sections">
                            <div className="detail-section">
                              <h4>Student Details</h4>
                              <p>
                                <strong>Account:</strong>{" "}
                                {student.accountNumber || "N/A"}
                              </p>
                              <p>
                                <strong>Date of Birth:</strong>{" "}
                                {formatDate(student.dateOfBirth)}
                              </p>
                              <p>
                                <strong>Class:</strong> {student.class || "N/A"}
                              </p>
                              <p>
                                <strong>Start Date:</strong>{" "}
                                {formatDate(student.startDate)}
                              </p>
                              {student.enrollmentInfo?.joinedTerm && (
                                <p>
                                  <strong>Joined Term:</strong> Term{" "}
                                  {student.enrollmentInfo.joinedTerm}
                                </p>
                              )}
                              <p>
                                <strong>T-Shirt Size:</strong>{" "}
                                {student.tShirtSize || "N/A"}
                              </p>
                            </div>

                            <div className="detail-section">
                              <h4>Primary Contact</h4>
                              <p>
                                <strong>Name:</strong>{" "}
                                {student.primaryContact?.name || "N/A"}
                              </p>
                              <p>
                                <strong>Cell:</strong>{" "}
                                {student.primaryContact?.cellPhone || "N/A"}
                              </p>
                              <p>
                                <strong>Email:</strong>{" "}
                                {student.primaryContact?.email || "N/A"}
                              </p>
                              <p>
                                <strong>Relationship:</strong>{" "}
                                {student.primaryContact?.relationship || "N/A"}
                              </p>
                            </div>

                            <div className="detail-section">
                              <h4>Payment Breakdown</h4>
                              {(() => {
                                const breakdown = calculateTermBreakdown(
                                  student,
                                  student._termStructure,
                                );

                                return (
                                  <div
                                    style={{
                                      overflowX: "auto",
                                      marginTop: "10px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        minWidth: "360px",
                                      }}
                                    >
                                      {/* Header Row */}
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "120px 80px 80px 80px",
                                          padding: "8px 0",
                                          borderBottom: "2px solid #333",
                                          fontSize: "12px",
                                          fontWeight: "700",
                                          color: "#333",
                                          backgroundColor: "#f8f9fa",
                                        }}
                                      >
                                        <span></span>
                                        <span style={{ textAlign: "right" }}>
                                          DUE
                                        </span>
                                        <span style={{ textAlign: "right" }}>
                                          PAID
                                        </span>
                                        <span style={{ textAlign: "right" }}>
                                          BALANCE
                                        </span>
                                      </div>

                                      {/* Admin Fee Row */}
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "120px 80px 80px 80px",
                                          padding: "8px 0",
                                          borderBottom: "1px solid #ddd",
                                          fontWeight: "600",
                                          backgroundColor: "#fffbf0",
                                        }}
                                      >
                                        <span>
                                          <strong>Admin Fee</strong>
                                        </span>
                                        <span style={{ textAlign: "right" }}>
                                          {formatCurrency(breakdown.admin.due)}
                                        </span>
                                        <span
                                          style={{
                                            textAlign: "right",
                                            color:
                                              breakdown.admin.paid > 0
                                                ? "#27ae60"
                                                : "#666",
                                          }}
                                        >
                                          {formatCurrency(breakdown.admin.paid)}
                                        </span>
                                        <span
                                          style={{
                                            textAlign: "right",
                                            color:
                                              breakdown.admin.balance > 0
                                                ? "#e74c3c"
                                                : "#27ae60",
                                            fontWeight: "700",
                                          }}
                                        >
                                          {formatCurrency(
                                            breakdown.admin.balance,
                                          )}
                                        </span>
                                      </div>

                                      {/* Term Rows */}
                                      {breakdown.terms.map((term) => (
                                        <div
                                          key={term.termNum}
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                              "120px 80px 80px 80px",
                                            padding: "6px 0",
                                            borderBottom: "1px solid #eee",
                                            opacity: term.shouldCharge
                                              ? 1
                                              : 0.5,
                                          }}
                                        >
                                          <span>
                                            <strong>Term {term.termNum}</strong>
                                            {!term.shouldCharge &&
                                              term.due === 0 && (
                                                <span
                                                  style={{
                                                    fontSize: "11px",
                                                    color: "#999",
                                                    marginLeft: "5px",
                                                  }}
                                                >
                                                  (not applicable)
                                                </span>
                                              )}
                                          </span>
                                          <span style={{ textAlign: "right" }}>
                                            {formatCurrency(term.due)}
                                          </span>
                                          <span
                                            style={{
                                              textAlign: "right",
                                              color:
                                                term.paid > 0
                                                  ? "#27ae60"
                                                  : "#666",
                                            }}
                                          >
                                            {formatCurrency(term.paid)}
                                          </span>
                                          <span
                                            style={{
                                              textAlign: "right",
                                              color:
                                                term.balance > 0
                                                  ? "#e74c3c"
                                                  : term.balance < 0
                                                    ? "#3498db"
                                                    : "#27ae60",
                                              fontWeight:
                                                term.balance !== 0
                                                  ? "600"
                                                  : "normal",
                                            }}
                                          >
                                            {formatCurrency(term.balance)}
                                          </span>
                                        </div>
                                      ))}

                                      {/* Total Row */}
                                      <div
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "120px 80px 80px 80px",
                                          padding: "10px 0",
                                          marginTop: "5px",
                                          borderTop: "2px solid #333",
                                          fontWeight: "700",
                                          fontSize: "15px",
                                        }}
                                      >
                                        <span>TOTAL</span>
                                        <span style={{ textAlign: "right" }}>
                                          {formatCurrency(
                                            breakdown.admin.due +
                                              breakdown.terms.reduce(
                                                (sum, t) => sum + t.due,
                                                0,
                                              ),
                                          )}
                                        </span>
                                        <span
                                          style={{
                                            textAlign: "right",
                                            color: "#27ae60",
                                          }}
                                        >
                                          {formatCurrency(
                                            student.totalPaid || 0,
                                          )}
                                        </span>
                                        <span
                                          style={{
                                            textAlign: "right",
                                            color:
                                              student.calculatedOwing > 0
                                                ? "#e74c3c"
                                                : "#27ae60",
                                            fontSize: "16px",
                                          }}
                                        >
                                          {formatCurrency(
                                            student.calculatedOwing || 0,
                                          )}
                                        </span>
                                      </div>

                                      {/* Status Indicators */}
                                      <div
                                        style={{
                                          marginTop: "10px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {breakdown.admin.balance === 0 && (
                                          <p
                                            style={{
                                              color: "#27ae60",
                                              marginBottom: "5px",
                                            }}
                                          >
                                            <strong>
                                              {"\u2705"} Admin Fee Fully Paid
                                            </strong>
                                          </p>
                                        )}
                                        {student.creditBalance > 0 && (
                                            <p
                                              style={{
                                                color: "#3498db",
                                                marginBottom: "5px",
                                              }}
                                            >
                                              <strong>
                                                {"\u{1F4B0}"} Credit Balance:
                                              </strong>{" "}
                                              {formatCurrency(
                                                student.creditBalance,
                                              )}
                                            </p>
                                          )}
                                        {student.lastPaymentDate && (
                                          <p
                                            style={{
                                              color: "#666",
                                              marginTop: "8px",
                                            }}
                                          >
                                            <strong>Last Payment:</strong>{" "}
                                            {formatDate(
                                              student.lastPaymentDate,
                                            )}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {markingInactive && (
        <MarkInactive
          student={markingInactive}
          onClose={() => {
            setMarkingInactive(null);
            loadData();
          }}
        />
      )}

      {reactivatingStudent && (
        <ReactivateStudent
          student={reactivatingStudent}
          onClose={() => {
            setReactivatingStudent(null);
            loadData();
          }}
        />
      )}

      {overridingStudent && (
        <OverrideFees
          student={overridingStudent}
          onClose={() => {
            setOverridingStudent(null);
            loadData();
          }}
        />
      )}

      {/* â† ADDED THIS MODAL */}
      {editingStudent && (
        <EditStudent
          student={editingStudent}
          onStudentUpdated={() => {
            setEditingStudent(null);
            loadData();
          }}
          onCancel={() => setEditingStudent(null)}
        />
      )}
    </div>
  );
}

export default ViewStudents;
